import { Bookmark, Category } from '../types/bookmark'

interface ExportOptions {
  format: 'json' | 'html' | 'csv'
  includeMetadata?: boolean
  dateFormat?: string
}

export class BookmarkExporter {
  private bookmarks: Bookmark[]
  private categories: Category[]

  constructor(bookmarks: Bookmark[], categories: Category[]) {
    this.bookmarks = bookmarks
    this.categories = categories
  }

  private formatDate(timestamp: number, format: string = 'yyyy-MM-dd HH:mm:ss'): string {
    const date = new Date(timestamp)
    const pad = (n: number) => n.toString().padStart(2, '0')

    return format
      .replace('yyyy', date.getFullYear().toString())
      .replace('MM', pad(date.getMonth() + 1))
      .replace('dd', pad(date.getDate()))
      .replace('HH', pad(date.getHours()))
      .replace('mm', pad(date.getMinutes()))
      .replace('ss', pad(date.getSeconds()))
  }

  private getCategoryName(categoryId?: string): string {
    if (!categoryId) return '未分类'
    return this.categories.find(c => c.id === categoryId)?.name || '未分类'
  }

  private toJSON(includeMetadata: boolean): string {
    const data = {
      bookmarks: this.bookmarks,
      categories: this.categories,
      ...(includeMetadata && {
        metadata: {
          exportDate: new Date().toISOString(),
          totalBookmarks: this.bookmarks.length,
          totalCategories: this.categories.length,
          version: '1.0.0'
        }
      })
    }
    return JSON.stringify(data, null, 2)
  }

  private toHTML(): string {
    const header = `<!DOCTYPE NETSCAPE-Bookmark-file-1>
<!-- This is an automatically generated file.
     It will be read and overwritten.
     DO NOT EDIT! -->
<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">
<TITLE>Bookmarks</TITLE>
<H1>Bookmarks</H1>
<DL><p>`

    const footer = '</DL><p>'

    // 按分类组织书签
    const bookmarksByCategory = new Map<string, Bookmark[]>()
    this.bookmarks.forEach(bookmark => {
      const categoryName = this.getCategoryName(bookmark.categoryId)
      if (!bookmarksByCategory.has(categoryName)) {
        bookmarksByCategory.set(categoryName, [])
      }
      bookmarksByCategory.get(categoryName)!.push(bookmark)
    })

    // 生成HTML内容
    let content = ''
    bookmarksByCategory.forEach((bookmarks, categoryName) => {
      content += `\n    <DT><H3>${categoryName}</H3>\n    <DL><p>\n`
      bookmarks.forEach(bookmark => {
        const tags = bookmark.tags.length > 0 ? ` TAGS="${bookmark.tags.join(',')}"` : ''
        const description = bookmark.description ? ` DESCRIPTION="${bookmark.description}"` : ''
        content += `        <DT><A HREF="${bookmark.url}" ADD_DATE="${Math.floor(bookmark.createdAt / 1000)}" LAST_MODIFIED="${Math.floor(bookmark.updatedAt / 1000)}"${tags}${description}>${bookmark.title}</A>\n`
      })
      content += '    </DL><p>\n'
    })

    return header + content + footer
  }

  private toCSV(dateFormat: string): string {
    const headers = ['标题', 'URL', '描述', '分类', '标签', '收藏', '创建时间', '更新时间']
    const rows = [headers]

    this.bookmarks.forEach(bookmark => {
      rows.push([
        bookmark.title,
        bookmark.url,
        bookmark.description || '',
        this.getCategoryName(bookmark.categoryId),
        bookmark.tags.join(';'),
        bookmark.isFavorite ? '是' : '否',
        this.formatDate(bookmark.createdAt, dateFormat),
        this.formatDate(bookmark.updatedAt, dateFormat),
      ])
    })

    // 处理CSV中的特殊字符
    const escapeCSV = (field: string) => {
      if (field.includes('"') || field.includes(',') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`
      }
      return field
    }

    return rows.map(row => row.map(escapeCSV).join(',')).join('\n')
  }

  export(options: ExportOptions): { content: string; filename: string } {
    const timestamp = this.formatDate(Date.now(), 'yyyyMMdd_HHmmss')
    let content: string
    let extension: string

    switch (options.format) {
      case 'json':
        content = this.toJSON(options.includeMetadata ?? true)
        extension = 'json'
        break
      case 'html':
        content = this.toHTML()
        extension = 'html'
        break
      case 'csv':
        content = this.toCSV(options.dateFormat || 'yyyy-MM-dd HH:mm:ss')
        extension = 'csv'
        break
      default:
        throw new Error('不支持的导出格式')
    }

    return {
      content,
      filename: `bookmarks_${timestamp}.${extension}`,
    }
  }
} 